
import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

interface VerificationFormProps {
  token: string | null;
  email: string;
}

export const VerificationForm = ({ token, email }: VerificationFormProps) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [textAlerts, setTextAlerts] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter the 6-digit code sent to your phone",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch("https://api.discoveryconciergeservices.com/api/v1/disco-auth/mobile-verify/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Token ${token}` : "", 
        },
        body: JSON.stringify({ 
          code: verificationCode,
          text_alerts_opt_in: textAlerts 
        }),
      });
      
      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        // Check content type to determine how to handle the error
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Verification failed");
        } else {
          // Handle non-JSON responses with status and statusText
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
      
      // Only try to parse JSON if we know it's JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        await response.json(); // We don't need the data, but parsing it to confirm it's valid
      }
      
      toast({
        title: "Verification successful",
        description: "Your account has been verified",
      });
      
      // Navigate to the vehicle registration page
      navigate("/vehicle-registration");
    } catch (error) {
      console.error("Verification error:", error);
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerify} className="space-y-6">
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={verificationCode}
          onChange={(value) => {
            console.log("OTP changed:", value);
            setVerificationCode(value);
          }}
        >
          <InputOTPGroup>
            {Array.from({ length: 6 }).map((_, index) => (
              <InputOTPSlot key={index} index={index} />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-gradient-to-r from-green-400 to-teal-400 hover:opacity-90"
        disabled={isLoading || verificationCode.length !== 6}
      >
        {isLoading ? "Verifying..." : "Verify"} 
        {!isLoading && <ArrowRight className="ml-2" size={16} />}
      </Button>

      <div className="flex items-start space-x-3 mt-4">
        <Checkbox 
          id="text-alerts" 
          checked={textAlerts} 
          onCheckedChange={(checked) => setTextAlerts(checked === true)} 
          className="mt-1 h-5 w-5 border-2 border-gray-300 bg-white"
        />
        <label htmlFor="text-alerts" className="text-xs text-black">
          By verifying your phone number you are opting-in to receiving text alerts from the DISCO platform about New Trips, Upcoming Trips, and Completed Trips
        </label>
      </div>
    </form>
  );
};
